import BaseService from './BaseService'
import { api } from '../boot/axios'

class PaymentService extends BaseService {
  constructor() {
    super('/payments')
  }

  // Get active gateway accounts (for terminal creation options)
  async getActiveGatewayAccounts() {
    return this.customRequest('get', '/gateways')
  }

  // Create payment intent (legacy - kept for backward compatibility)
  async createPaymentIntent(amount, currency = 'usd', saleId) {
    return this.customRequest('post', '/create-intent', {
      amount: Math.round(amount * 100), // Convert to cents
      currency,
      sale_id: saleId
    })
  }

  // Confirm payment (legacy - kept for backward compatibility)
  async confirmPayment(paymentMethodId, customerId = null, paymentDetails = null) {
    return this.customRequest('post', '/confirm', {
      payment_method_id: paymentMethodId,
      customer_id: customerId,
      payment_method_type: 'card',
      ...(paymentDetails || {})
    })
  }

  // NEW: Generic card payment endpoint (works with any gateway)
  async processCardPayment(params) {
    const body = {
      sale_id: params.saleId,
      amount: params.amount,
      tip_amount: params.tipAmount || 0,
      tax_amount: params.taxAmount || 0,
      terminal_id: params.terminalId, // For physical terminal
      customer_payment_method_id: params.customerPaymentMethodId, // For card on file
      customer_id: params.customerId, // Required for COF when sale customer may differ
      notes: params.notes
    }
    if (params.processedExternally !== undefined) {
      body.processed_externally = params.processedExternally
    }
    if (params.gatewayReference !== undefined) {
      body.gateway_reference = params.gatewayReference
    }
    if (params.gatewayMetadata !== undefined) {
      body.gateway_metadata = params.gatewayMetadata
    }
    if (params.paymentTokenId !== undefined) {
      body.payment_token_id = params.paymentTokenId
    }
    if (params.stripePaymentMethodId !== undefined) {
      body.stripe_payment_method_id = params.stripePaymentMethodId
    }
    if (params.cardholder !== undefined && params.cardholder !== null && params.cardholder !== '') {
      body.cardholder = params.cardholder
    }
    if (params.tabId != null) {
      body.tab_id = params.tabId
    }
    if (Array.isArray(params.ledgerEntryIds) && params.ledgerEntryIds.length > 0) {
      body.ledger_entry_ids = params.ledgerEntryIds
    }
    return this.customRequest(
      'post',
      '/card',
      body,
      {
        timeout: params.timeout || 120000 // Allow custom timeout, default to 120s
      })
  }

  // Quote convenience fee for a would-be card payment (for UI previews)
  async quoteConvenienceFee ({ amount, tipAmount = 0, terminalId = null, gateway = null, saleId = null, invoiceId = null, tabId = null, ledgerEntryIds = null, items = null }) {
    const body = {
      amount,
      tip_amount: tipAmount || 0
    }
    if (terminalId != null) {
      body.terminal_id = terminalId
    }
    if (gateway != null) {
      body.gateway = gateway
    }
    if (saleId != null) {
      body.sale_id = saleId
    }
    if (invoiceId != null) {
      body.invoice_id = invoiceId
    }
    if (tabId != null) {
      body.tab_id = tabId
    }
    if (Array.isArray(ledgerEntryIds) && ledgerEntryIds.length > 0) {
      body.ledger_entry_ids = ledgerEntryIds
    }
    if (Array.isArray(items) && items.length > 0) {
      body.items = items
    }
    return this.customRequest('post', '/convenience-fee/quote', body)
  }

  async registerDejavooMobileTerminal(payload) {
    return this.customRequest('post', '/mobile-terminals/dejavoo', payload)
  }

  // Handle refunds
  async refundPayment(paymentId, amount = null) {
    return this.customRequest('post', '/refund', {
      payment_id: paymentId,
      amount
    })
  }

  // Get payment history
  async getPaymentHistory(filters = {}) {
    return this.customRequest('get', '/history', filters)
  }

  // Get payment details
  async getPaymentDetails(paymentId) {
    return this.customRequest('get', `/details/${paymentId}`)
  }

  // NEW: Record an externally-processed payment (e.g., Dejavoo)
  async recordExternalPayment({
    saleId,
    amount,
    tipAmount = 0,
    taxAmount = 0,
    method = 'card',
    gateway = 'dejavoo',
    gatewayReference = null,
    gatewayMetadata = null,
    notes = 'External terminal payment'
  }) {
    return this.customRequest('post', '/external', {
      sale_id: saleId,
      amount,
      tip_amount: tipAmount,
      tax_amount: taxAmount,
      method,
      gateway,
      gateway_reference: gatewayReference,
      gateway_metadata: gatewayMetadata,
      notes
    })
  }

  // Get payment status
  async getPaymentStatus(paymentId) {
    return this.customRequest('get', `/status/${paymentId}`)
  }

  // Handle saved payment methods
  async savePaymentMethod(paymentMethodId, customerId) {
    return this.customRequest('post', '/save-method', {
      payment_method_id: paymentMethodId,
      customer_id: customerId
    })
  }

  async getSavedPaymentMethods(customerId) {
    return this.customRequest('get', `/saved-methods/${customerId}`)
  }

  async addCustomerPaymentMethod(customerId, payload) {
    const response = await api.post(`/customers/${customerId}/payment-methods`, payload)
    return response.data
  }

  async updateSavedPaymentMethod(paymentMethodId, payload) {
    return this.customRequest('patch', `/saved-methods/${paymentMethodId}`, payload)
  }

  async deleteSavedPaymentMethod(paymentMethodId) {
    return this.customRequest('delete', `/saved-methods/${paymentMethodId}`)
  }

  async storeSplitPlan ({ saleId, legs }) {
    return this.customRequest('post', '/split-plan', {
      sale_id: saleId,
      legs: (legs || []).map((leg) => ({
        method: leg.method === 'card' ? 'card' : 'cash',
        amount: leg.amount,
        label: leg.label || undefined,
        terminal_id: leg.terminalId || leg.terminal_id || undefined
      }))
    })
  }

  async processSplitLeg (params) {
    const body = {
      sale_id: params.saleId,
      leg_index: params.legIndex,
      method: params.method,
      amount: params.amount,
      amount_received: params.amountReceived,
      tax_amount: params.taxAmount,
      terminal_id: params.terminalId,
      notes: params.notes,
      tip_amount: params.tipAmount,
      payment_token_id: params.paymentTokenId,
      cardholder: params.cardholder,
      processed_externally: params.processedExternally,
      gateway_reference: params.gatewayReference,
      gateway_metadata: params.gatewayMetadata
    }
    Object.keys(body).forEach((key) => {
      if (body[key] === undefined) delete body[key]
    })
    return this.customRequest('post', '/split-leg', body)
  }

  // Legacy batch split (cash + at most one terminal leg)
  async processSplitPayment ({ saleId, payments, total }) {
    // Validate total amount matches
    const totalAmount = payments.reduce((sum, payment) => sum + payment.amount, 0)
    if (Math.abs(totalAmount - total) > 0.01) {
      throw new Error('Split payment amounts must equal the total')
    }

    return this.customRequest('post', '/split', {
      sale_id: saleId,
      payments: payments.map(payment => {
        const method = String(payment.method || '').toLowerCase()
        const apiMethod = method === 'card' ? 'terminal' : method
        return {
          method: apiMethod,
          amount: payment.amount,
          amount_received: payment.receivedAmount,
          terminal_id: payment.terminalId,
          tax_amount: payment.taxAmount,
          notes: payment.notes
        }
      })
    })
  }

  // Process cash payment
  async processCashPayment(params) {
    return this.customRequest('post', '/cash', {
      sale_id: params.sale_id,
      amount: params.amount,
      amount_received: params.amount_received
    })
  }

  // Check terminal connection status
  async checkTerminalConnection(terminalId) {
    const response = await this.customRequest('get', `/terminal/${terminalId}/connection`)
    // Extract connection_status from the response
    return response?.connection_status || response
  }

  // NEW: Get terminals for current location
  async getTerminalsForLocation(locationId = null) {
    const params = locationId ? { location_id: locationId } : null
    const response = await this.customRequest('get', '/terminals/location', params)
    // Extract terminals array from the response
    return response?.terminals || []
  }

  // NEW: Get terminal info
  async getTerminalInfo(terminalId) {
    const response = await this.customRequest('get', `/terminal/${terminalId}/info`)
    // Extract terminal_info from the response
    return response?.terminal_info || null
  }

  // NEW: Get terminal transaction summary
  async getTerminalTransactionSummary(terminalId, date = null) {
    const params = date ? { date } : {}
    const response = await this.customRequest('get', `/terminal/${terminalId}/summary`, params)
    // Extract summary from the response
    return response?.summary || null
  }

  // NEW: Get terminal status for a specific register
  async getTerminalStatusForRegister(registerId) {
    const response = await this.customRequest('get', `/register/${registerId}/terminal-status`)
    return response
  }

  // NEW: Get all registers with their terminal assignments for a location
  async getRegistersWithTerminals(locationId = null) {
    const params = locationId ? { location_id: locationId } : null
    const response = await this.customRequest('get', '/registers-with-terminals', params)
    return response?.registers_with_terminals || []
  }

  // NEW: Get all unassigned terminals
  async getUnassignedTerminals() {
    const response = await this.customRequest('get', '/terminals/unassigned')
    return response?.terminals || []
  }

  // Create a new payment terminal
  async createTerminal({
    label,
    external_id,
    gateway,
    status,
    credentials = {},
    capabilities = [],
    location_id = null,
    sales_register_id = null,
    merchant_code = null,
    ecom_auth_token = null
  }) {
    const payload = {
      gateway,
      external_id,
      label,
      status,
      credentials,
      capabilities,
      location_id,
      sales_register_id
    }
    if (merchant_code !== null && merchant_code !== undefined && String(merchant_code).trim() !== '') {
      payload.merchant_code = merchant_code
    }
    if (ecom_auth_token !== null && ecom_auth_token !== undefined && String(ecom_auth_token).trim() !== '') {
      payload.ecom_auth_token = ecom_auth_token
    }
    return this.customRequest('post', '/terminals', payload)
  }

  // Update an existing payment terminal
  async updateTerminal(id, {
    label,
    external_id,
    gateway,
    status,
    credentials = {},
    capabilities = [],
    location_id = null,
    sales_register_id = null,
    merchant_code = null,
    ecom_auth_token = null
  }) {
    const payload = {
      gateway,
      external_id,
      label,
      status,
      credentials,
      capabilities,
      location_id,
      sales_register_id
    }
    if (merchant_code !== null && merchant_code !== undefined) {
      payload.merchant_code = merchant_code === '' ? '' : merchant_code
    }
    if (ecom_auth_token !== null && ecom_auth_token !== undefined) {
      payload.ecom_auth_token = ecom_auth_token === '' ? '' : ecom_auth_token
    }
    return this.customRequest('put', `/terminals/${id}`, payload)
  }
}

export const paymentService = new PaymentService()
